/**
 * DropZone — accessible document upload control.
 *
 * Wraps a visually-hidden `<input type="file">` inside a `<label>`. Keyboard
 * users tab to the input, press Space to open the native file picker. Drop
 * target is a sibling `<div aria-hidden="true">` — decorative only.
 *
 * Fixes WCAG 2.1.1 FAIL on mockup #17 (rae-booking-step4-documents.html drag-drop).
 * Per rcp-security §B.5 #4.
 *
 * Announces successful upload via the live-region passed in via
 * `onAnnounce` (wired by parent Page using AccessibleToast).
 */

import { useCallback, useId, useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';

export interface DropZoneProps {
  label: string;
  accept?: string;
  multiple?: boolean;
  maxSizeMb?: number;
  onFile: (file: File) => void | Promise<void>;
  onError?: (message: string) => void;
  onAnnounce?: (message: string) => void;
  hint?: string;
}

export function DropZone(props: DropZoneProps): JSX.Element {
  const {
    label,
    accept = 'image/jpeg,image/png,image/heic,application/pdf',
    multiple = false,
    maxSizeMb = 25,
    onFile,
    onError,
    onAnnounce,
    hint,
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();
  const hintId = useId();
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | null): Promise<void> => {
      if (!files || files.length === 0) return;
      const list = Array.from(files);
      for (const f of list) {
        if (f.size > maxSizeMb * 1024 * 1024) {
          onError?.(`${f.name} is too large — the limit is ${maxSizeMb} MB.`);
          continue;
        }
        await Promise.resolve(onFile(f));
        onAnnounce?.(`Uploaded ${f.name}, ${Math.round(f.size / 1024)} kilobytes.`);
      }
    },
    [maxSizeMb, onFile, onError, onAnnounce],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      void handleFiles(e.dataTransfer?.files ?? null);
    },
    [handleFiles],
  );

  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <div
        className="card"
        style={{
          padding: 24,
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: isDragging ? 'var(--pcb-blue)' : 'var(--surface-border)',
          background: isDragging ? 'var(--surface-info-soft)' : 'var(--surface-info-bg)',
          textAlign: 'center',
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        aria-hidden="true"
      >
        <UploadCloud aria-hidden="true" size={36} color="var(--pcb-blue)" />
        <p style={{ margin: '8px 0 4px 0', color: 'var(--pcb-blue-dark)', fontWeight: 500 }}>
          Drag files here or click to browse
        </p>
        <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: 13 }}>
          {hint ?? `JPG, PNG, HEIC, or PDF — up to ${maxSizeMb} MB each`}
        </p>
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        aria-describedby={hintId}
        onChange={(e) => void handleFiles(e.target.files)}
        className="visually-hidden"
      />
      <span id={hintId} className="hint">
        Press Space or Enter on the <strong>{label}</strong> input to open your
        file picker. Files are uploaded securely over HTTPS.
      </span>
    </div>
  );
}
